<template>
	<div class="time-traveller hidden-print">
		<button
			id="time-traveller-previous"
			class="btn btn-unstyled"
			:disabled="prevExceeded"
			@click="getPrevious()"
		>
			<font-awesome-icon icon="arrow-circle-left"></font-awesome-icon> <span>{{ $t('MAIN_CONTENT.commonContent.previous') }}</span>
		</button>

		<div class="flex align-items-center">
			<strong> {{ $t('MAIN_CONTENT.TimeTraveller.weekOfWithColon') }}&nbsp;</strong>
			<div style="display:inline-block">
				<date-picker
					name="selectedDate"
					v-model="displayDate"
					:aria-label="`${ $t('MAIN_CONTENT.TimeTraveller.weekOfWithColon') } ${ $t( getDisplayText ) }`"
					:describedByID="'weekOfDateInput'"
					:datePickerInputMessage="$t( 'MAIN_CONTENT.TimeTraveller.datepickerAriaMessage' )"
					:label="`${ $t('MAIN_CONTENT.TimeTraveller.weekOfWithColon') } ${ $t( getDisplayText ) }`"
					:disabled="false"
					:notBefore="getBeforeRange()"
					:notAfter="getAfterRange()"
					:locale="getSelectedLocale.LOCALE_CODE !== 'sa-us' ? getSelectedLocale.LOCALE_CODE : 'en-US'"
					format="MM/DD/YYYY"
					ref="selectedDatePickerInput"
					lang="en"
					:showLabel="false"
					:clearable="false"
				>
				</date-picker>
			</div>
		</div>

		<button class="btn btn-unstyled"
						:disabled="nextExceeded"
						@click="getNext()">
			<span>{{ $t('MAIN_CONTENT.commonContent.next') }}</span> <font-awesome-icon icon="arrow-circle-right"></font-awesome-icon>
		</button>
	</div>
</template>

<script>
	import moment from 'moment';
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

	// Shared components in vuecommons
	import DatePicker from "COMMONS/shared/AccessabilityDatePicker.vue";
	import { mapGetters } from 'vuex';

	export default {
		name: 'TimeTraveller',
		props: {
			dateOptions: {
				type:			Object,
				required:	true
			},
			defaultType: {
				type:			String,
				default:	'weekly'
			},
			expandedDate: {
				type:			String,
				default:	moment().format('dddd, MMMM D, YYYY')
			}
		},
		components: {
			DatePicker,
			FontAwesomeIcon
		},
		data() {
			return {
				selectedDate:			moment(this.dateOptions.SELECTED_DATE, 'MM/DD/YYYY'),
				displayDate:			moment(this.dateOptions.MIN_DATE, 'MM/DD/YYYY')._d,
				// school week start day: range 0 (Sunday) to 6 (Saturday).
				schoolYearStart:	moment( this.dateOptions.SCHOOL_YEAR_START, 'MM/DD/YYYY'),
				schoolYearEnd:		moment( this.dateOptions.SCHOOL_YEAR_END, 'MM/DD/YYYY'),
				// date item date settings
				startDate:				null,
				endDate:					null,
				minDate:					moment(this.dateOptions.MIN_DATE, 'MM/DD/YYYY'),
				maxDate:					moment(this.dateOptions.MAX_DATE, 'MM/DD/YYYY'),
				prevExceeded:			false,
				nextExceeded:			false
			};
		},
		computed: {
			...mapGetters([ 'getSelectedLocale', 'profileLocale' ]),
			getDisplayText() {
				this.getSelectedLocale.LOCALE_CODE !== 'sa-us' && 
				this.getSelectedLocale.LOCALE_CODE !== 'en-US' ? 
					moment.locale( this.getSelectedLocale.LOCALE_CODE ) : 
					moment.locale( 'en' );

				return this.displayDate._isValid || this.displayDate._isValid === undefined ?
					moment( this.displayDate ).format( "dddd, MMMM D, YYYY" ).toString() :
					this.$t( 'MAIN_CONTENT.TimeTraveller.enterCorrectDateFormat' );
			}
		},
		watch: {
			displayDate( newDate, oldDate ) {
				if( newDate !== oldDate ) {
					let date = moment( this.displayDate );

					if( date > moment( this.dateOptions.SCHOOL_YEAR_END ) ) {
						date = moment( this.dateOptions.SCHOOL_YEAR_END );
						this.displayDate = date;
					} else if( date < moment( this.dateOptions.SCHOOL_YEAR_START ) ) {
						date = moment( this.dateOptions.SCHOOL_YEAR_START );
						this.displayDate = date;
					}

					let startDate = moment( date );
					let endDate = moment( date );
					let minDate = moment( date );
					let maxDate = moment( date );

					switch( date.day() ) {
						// Sunday
						case 0:
							// Go up a day to get Monday and Friday
							minDate = minDate.valueOf( minDate.add( 1, "days" ) );
							maxDate = maxDate.valueOf( maxDate.add( 5, "days" ) );
							startDate = startDate.valueOf( startDate.add( 1, "days" ) );
							endDate = endDate.valueOf( endDate.add( 5, "days" ) );
							break;
						// Monday, just need to get Friday
						case 1:
							minDate = minDate.valueOf( minDate );
							maxDate = maxDate.valueOf( maxDate.add( 4, "days" ) );
							startDate = startDate.valueOf( startDate );
							endDate = endDate.valueOf( endDate.add( 4, "days" ) );
							break;
						// Tuesday, need to get Monday and Friday
						case 2:
							minDate = minDate.valueOf( minDate.subtract( 1, "days" ) );
							maxDate = maxDate.valueOf( maxDate.add( 3, "days" ) );
							startDate = startDate.valueOf( startDate.subtract( 1, "days" ) );
							endDate = endDate.valueOf( endDate.add( 3, "days" ) );
							break;
						// Wednesday, need to get Monday and Friday
						case 3:
							minDate = minDate.valueOf( minDate.subtract( 2, "days" ) );
							maxDate = maxDate.valueOf( maxDate.add( 2, "days" ) );
							startDate = startDate.valueOf( startDate.subtract( 2, "days" ) );
							endDate = endDate.valueOf( endDate.add( 2, "days" ) );
							break;
						// Thursday, need to get Monday and Friday
						case 4:
							minDate = minDate.valueOf( minDate.subtract( 3, "days" ) );
							maxDate = maxDate.valueOf( maxDate.add( 1, "days" ) );
							startDate = startDate.valueOf( startDate.subtract( 3, "days" ) );
							endDate = endDate.valueOf( endDate.add( 1, "days" ) );
							break;
						// Friday, need to get Monday
						case 5:
							minDate = minDate.valueOf( minDate.subtract( 4, "days" ) );
							maxDate = maxDate.valueOf( maxDate );
							startDate = startDate.valueOf( startDate.subtract( 4, "days" ) );
							endDate = endDate.valueOf( endDate );
							break;
						// Saturday, need to get Monday and Friday
						case 6:
							minDate = minDate.valueOf( minDate.add( 2, "days" ) );
							maxDate = maxDate.valueOf( maxDate.add( 6, "days" ) );
							startDate = startDate.valueOf( startDate.add( 2, "days" ) );
							endDate = endDate.valueOf( endDate.add( 6, "days" ) );
							// go down to get Monday
							break;
					}

					// Format date to MM/DD/YYYY
					minDate = moment( minDate ).format( 'MM/DD/YYYY' );
					maxDate = moment( maxDate ).format( 'MM/DD/YYYY' );

					this.minDate = moment( minDate, 'MM/DD/YYYY' );
					this.maxDate = moment( maxDate, 'MM/DD/YYYY' );
					this.startDate = moment( startDate );
					this.endDate = moment( endDate );

					if( ( moment( this.minDate ).diff( moment( this.dateOptions.SCHOOL_YEAR_START ), "years" ) ) < 0 ) {
						this.minDate = moment(this.dateOptions.SCHOOL_YEAR_START);
						this.startDate = moment(this.dateOptions.SCHOOL_YEAR_START);
						this.maxDate = moment(this.dateOptions.SCHOOL_YEAR_START).add(6, 'days');
						this.endDate = moment(this.dateOptions.SCHOOL_YEAR_START).add(6, 'days');
						this.displayDate = moment(this.dateOptions.SCHOOL_YEAR_START);
					}
					// emit back to main component to update dates and make new API calls
					this.$emit(
						"timeTravel",
						moment( this.startDate ).format( "MM/DD/YYYY" ),
						moment( this.endDate ).format( "MM/DD/YYYY" )
					);
					this.setDateBounds();
				}
			}
		},
		methods: {
			initDates() {
				// set "today"
				this.selectedDate = moment(this.dateOptions.SELECTED_DATE);
				this.startDate = moment(Object.assign({}, this.selectedDate)).startOf('weeks');
				this.endDate = moment(Object.assign({}, this.selectedDate)).endOf('weeks');
			},
			getPrevious() {
				// Set the data objects date values to be moment JS date objects
				this.displayDate = moment( this.displayDate ).subtract( 7, "days" ).format( "MM/DD/YYYY" );
				this.startDate = moment( this.stratDate ).subtract( 7, "days" );
				this.endDate = moment( this.endDate ).subtract( 7, "days" );
				this.selectedDate = moment( this.selectedDate ).subtract( 7, "days" );
				this.minDate = moment( this.minDate ).subtract( 7, "days" );
				this.maxDate = moment( this.maxDate ).subtract( 7, "days" );
			},
			getNext() {
				// Set the data objects date values to be moment JS date objects
				this.displayDate = moment( this.displayDate ).add( 7, "days" ).format('MM/DD/YYYY');
				this.startDate = moment( this.startDate ).add( 7, "days" );
				this.endDate = moment( this.endDate ).add( 7, "days" );
				this.selectedDate = moment( this.selectedDate ).add( 7, "days" );
				this.minDate = moment( this.minDate ).add( 7, "days" );
				this.maxDate = moment( this.maxDate ).add( 7, "days" );
			},
			getBeforeRange() {
				return moment( this.dateOptions.SCHOOL_YEAR_START );
			},
			getAfterRange() {
				return moment( this.dateOptions.SCHOOL_YEAR_END );
			},
			// setting boolean props for disabling buttons this way since Vue seems to have some trouble detecting changes in moment objects
			setDateBounds() {
				this.prevExceeded = this.schoolYearStart.isAfter(this.startDate);
				this.nextExceeded = this.schoolYearEnd.isBefore(this.endDate);
			}
		},
			// pass start and end dates to the parent component
		created() {
			this.initDates();
		}
	};
</script>

<style scoped>
	.flex {
		display: flex;
	}
	.align-items-center {
		align-items: center;
	}
	#time-traveller-previous {
		color: #327AB8;
	}
</style>
